import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import './Login.css';
import { Card, CardContent, CardHeader } from '@/src/shadComponents/ui/card';
import { Alert, AlertDescription } from '@/src/shadComponents/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/src/tailwindui/input';
import { Button } from '@/src/tailwindui/button';
import { Heading } from '@/src/tailwindui/heading';
import { Fieldset, Label } from '@/src/tailwindui/fieldset';
import { cn } from '@/src/lib/utils';
import logo from '../../pe-logo.svg';
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
} from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import ReCAPTCHA from 'react-google-recaptcha';

export default function CreateAccount() {
    const navigate = useNavigate();
    const [feedback, setFeedback] = useState<string | null>(null);
    const { createAccountAndLogin } = useAuth();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [organizationName, setOrganizationName] = useState<string>('');
    const [logoUrl] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');

    const [phaseOne, setPhaseOne] = useState<boolean>(true);
    const [phaseTwo, setPhaseTwo] = useState<boolean>(false);
    const [phaseThree, setPhaseThree] = useState<boolean>(false);
    const [selectedWork, setSelectedWork] = useState<string>('');
    const [selectedSource, setSelectedSource] = useState<string>('');
    const [verified, setVerified] = useState<boolean>(false);
    const creatingAccount = useRef<boolean>(false);

    useEffect(() => {
        setFeedback(null);
    }, [password, confirmPassword, email]);

    const onVerify = useCallback((token: string | null) => {
        console.log('Token:', token);
        setVerified(true);
        // @ts-ignore
    }, []);

    async function createAccount(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (creatingAccount.current) {
            return;
        }

        if (!verified) {
            setFeedback('Please verify that you are not a robot.');
            return;
        }

        creatingAccount.current = true;

        try {
            const authenticated = await createAccountAndLogin(
                email.toLowerCase(),
                password,
                firstName,
                lastName,
                organizationName,
                logoUrl,
                selectedWork,
                selectedSource,
            );

            if (authenticated) {
                navigate('/welcome', { replace: true });
            } else {
                setFeedback('Hmm, something went wrong. Please try again.');
            }
        } catch (e) {
            console.log('Error creating account', e);
            // @ts-ignore
            const response = e.response.data;
            setFeedback(response.message);
        } finally {
            creatingAccount.current = false;
        }
    }

    return (
        <>
            <div className="fixed bottom-4 right-4 ">
                <img
                    className="w-[140px] clickable cursor-pointer object-contain pe-logo"
                    src={logo}
                    alt="PhotoEngineers"
                />
            </div>
            <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
                <Card className="w-[90%] md:min-w-[600px] md:w-[600px] rounded-none shadow-none border-0 mb-[200px] md:mb-0">
                    <CardHeader>
                        <div
                            className={cn(
                                'fixed top-4 left-4 flex flex-col gap-5 transition-all duration-300',
                                phaseOne &&
                                    'h-0 m-0 p-0 opacity-0 overflow-hidden',
                            )}
                        >
                            <Heading
                                className="text-xl w-full cursor-pointer"
                                onClick={() => {
                                    if (phaseTwo) {
                                        setPhaseOne(true);
                                        setPhaseTwo(false);
                                    }

                                    if (phaseThree) {
                                        setPhaseTwo(true);
                                        setPhaseThree(false);
                                    }
                                }}
                            >
                                &larr;
                            </Heading>
                        </div>
                        <div
                            className={cn(
                                'flex flex-col gap-5',
                                !phaseOne &&
                                    'h-0 m-0 p-0 opacity-0 overflow-hidden',
                            )}
                        >
                            <Heading className="text-4xl md:text-5xl w-full plex-sans">
                                Create your free account
                            </Heading>
                            <p className="text-2xl md:text-3xl plex-sans-light w-full">
                                Get started with the most powerful tools to
                                shoot and share.
                            </p>
                        </div>

                        <div
                            className={cn(
                                'flex flex-col gap-5',
                                !phaseTwo &&
                                    'h-0 m-0 p-0 opacity-0 overflow-hidden',
                            )}
                        >
                            <Heading className="text-4xl md:text-5xl w-full plex-sans">
                                Tell us a little about yourself
                            </Heading>

                            <p className="text-2xl md:text-3xl plex-sans-light w-full">
                                Add your name and password to finish setting up
                                your account.
                            </p>
                        </div>

                        <div
                            className={cn(
                                'flex flex-col gap-5',
                                !phaseThree &&
                                    'h-0 m-0 p-0 opacity-0 overflow-hidden',
                            )}
                        >
                            <Heading className="text-4xl md:text-5xl plex-sans w-full">
                                Welcome, {firstName}.
                            </Heading>

                            <p className="text-2xl md:text-3xl plex-sans-light w-full">
                                Just a couple more questions to get to know you
                                better.
                            </p>
                        </div>
                    </CardHeader>
                    <CardContent className="mt-0">
                        <div className=" relative">
                            <div
                                id="buttonDiv"
                                className="flex justify-center"
                            ></div>

                            <div
                                className={cn(
                                    'mb-[30px] transition-all duration-300',
                                    !feedback && ' mb-0 h-0 opacity-0',
                                )}
                            >
                                <Alert variant="destructive">
                                    <div className="flex items-center">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription className="ml-2 monospace-font">
                                            {feedback}
                                        </AlertDescription>
                                    </div>
                                </Alert>
                            </div>
                            <div className="flex flex-col mb-4 w-full ">
                                <div
                                    className={cn(
                                        'transition-all duration-300 transition-delay-200 flex flex-col gap-5',
                                        !phaseOne &&
                                            'h-0 m-0 p-0 opacity-0 overflow-hidden',
                                    )}
                                >
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-xl">
                                            Email
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="text"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                        />
                                    </Fieldset>

                                    <Button
                                        type="submit"
                                        className="w-full black-button  disabled:opacity-50"
                                        disabled={!email}
                                        onClick={() => {
                                            if (!validateEmail(email)) {
                                                setFeedback(
                                                    "The email address you entered doesn't look valid.",
                                                );
                                                return;
                                            }

                                            setPhaseTwo(true);
                                            setPhaseOne(false);
                                            setPhaseThree(false);
                                        }}
                                    >
                                        <p className="text-xl">Continue</p>
                                    </Button>
                                </div>

                                <div
                                    className={cn(
                                        'transition-all duration-300 transition-delay-200 flex flex-col gap-2',
                                        !phaseTwo &&
                                            'h-0 m-0 p-0 opacity-0 overflow-hidden gap-0',
                                    )}
                                >
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            First Name
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="text"
                                            value={firstName}
                                            onChange={(e) =>
                                                setFirstName(e.target.value)
                                            }
                                        />
                                    </Fieldset>
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            Last Name
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="text"
                                            value={lastName}
                                            onChange={(e) =>
                                                setLastName(e.target.value)
                                            }
                                        />
                                    </Fieldset>
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            Password
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="password"
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                        />
                                    </Fieldset>
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg ">
                                            Confirm Password
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) =>
                                                setConfirmPassword(
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </Fieldset>
                                    <Button
                                        type="submit"
                                        className="w-full black-button disabled:opacity-50 mt-3"
                                        disabled={
                                            !email ||
                                            !password ||
                                            !confirmPassword
                                        }
                                        onClick={() => {
                                            if (password !== confirmPassword) {
                                                setFeedback(
                                                    'The passwords you entered do not match.',
                                                );
                                                return;
                                            }

                                            if (password.length < 8) {
                                                setFeedback(
                                                    'The password must be at least 8 characters long.',
                                                );
                                                return;
                                            }

                                            setPhaseThree(true);
                                            setPhaseOne(false);
                                            setPhaseTwo(false);
                                        }}
                                    >
                                        <p className="text-xl">Continue</p>
                                    </Button>
                                </div>

                                <div
                                    className={cn(
                                        'transition-all duration-300 transition-delay-200 flex flex-col gap-5',
                                        !phaseThree &&
                                            'h-0 m-0 p-0 opacity-0 overflow-hidden gap-0',
                                    )}
                                >
                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            What's the name of your business?
                                        </Label>
                                        <Input
                                            className="shadow-none monospace-font font-size-22"
                                            type="text"
                                            value={organizationName}
                                            onChange={(e) =>
                                                setOrganizationName(
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </Fieldset>

                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            What kind of work do you do?
                                        </Label>
                                        <div className="relative mt-2 pr-5">
                                            <Listbox
                                                value={selectedWork}
                                                onChange={setSelectedWork}
                                            >
                                                <ListboxButton className="flex flex-row justify-between w-full cursor-default grid-cols-1 rounded-md ring-1 bg-[#FFFFFF] dark:bg-[#262C2C] ring-[#333333] dark:ring-[#FFFFFF]  py-1.5 pr-2 pl-3 text-left outline-1 -outline-offset-1 outline-gray-600 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-300 sm:text-sm/6 px-2 py-2">
                                                    <span className="text-lg monospace-font text-black dark:text-white">
                                                        {selectedWork}&nbsp;
                                                    </span>
                                                    <ChevronUpDownIcon
                                                        aria-hidden="true"
                                                        className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                                    />
                                                </ListboxButton>
                                                <ListboxOptions
                                                    transition
                                                    className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
                                                >
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="I'm a photographer"
                                                    >
                                                        I'm a photographer
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="I do photo booths"
                                                    >
                                                        I do photo booths
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="I'm an entrepreneur"
                                                    >
                                                        I'm an entrepreneur
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="I produce events"
                                                    >
                                                        I produce events
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="I work at a brand/agency"
                                                    >
                                                        I work at a brand/agency
                                                    </ListboxOption>
                                                </ListboxOptions>
                                            </Listbox>
                                        </div>
                                    </Fieldset>

                                    <Fieldset>
                                        <Label className="plex-sans-bold text-lg">
                                            How did you hear about us?
                                        </Label>

                                        <div className="relative mt-2 pr-5">
                                            <Listbox
                                                value={selectedSource}
                                                onChange={setSelectedSource}
                                            >
                                                <ListboxButton className="flex flex-row justify-between w-full cursor-default grid-cols-1 rounded-md ring-1  bg-[#FFFFFF] dark:bg-[#262C2C] ring-[#000000] dark:ring-white py-1.5 pr-2 pl-3 text-left outline-1 -outline-offset-1 outline-gray-600 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-300 sm:text-sm/6 px-2 py-2">
                                                    <span className="text-lg monospace-font text-black dark:text-[#FFFFFF]">
                                                        {selectedSource} &nbsp;
                                                    </span>
                                                    <ChevronUpDownIcon
                                                        aria-hidden="true"
                                                        className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                                    />
                                                </ListboxButton>
                                                <ListboxOptions
                                                    transition
                                                    className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
                                                >
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="PBX"
                                                    >
                                                        PBX
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="WPPI"
                                                    >
                                                        WPPI
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="Social Media"
                                                    >
                                                        Social Media
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="Facebook Groups"
                                                    >
                                                        Facebook Groups
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="Google"
                                                    >
                                                        Google
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="Word of Mouth"
                                                    >
                                                        Word of Mouth
                                                    </ListboxOption>
                                                    <ListboxOption
                                                        className="group relative cursor-default monospace-font py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-200 data-focus:text-gray-900 data-focus:outline-hidden"
                                                        value="Other"
                                                    >
                                                        Other
                                                    </ListboxOption>
                                                </ListboxOptions>
                                            </Listbox>
                                        </div>
                                    </Fieldset>

                                    <ReCAPTCHA
                                        onChange={onVerify}
                                        sitekey={
                                            '6Ld6feAqAAAAAKw-3HJGFpjwKHnHOi_3ipUkKEQQ'
                                        }
                                    />

                                    <Button
                                        type="submit"
                                        className="w-full disabled:opacity-50 black-button mt-2  disabled:cursor-not-allowed disabled:pointer-events-none"
                                        disabled={
                                            !email ||
                                            !password ||
                                            !confirmPassword ||
                                            !organizationName ||
                                            !firstName ||
                                            !lastName ||
                                            !verified
                                        }
                                        onClick={(
                                            e: React.MouseEvent<HTMLButtonElement>,
                                        ) => {
                                            createAccount(e);
                                        }}
                                    >
                                        <p className="text-xl">
                                            Create my account
                                        </p>
                                    </Button>
                                </div>
                                <div className="flex justify-start mt-2">
                                    <p className="text-sm text-gray-500">
                                        By creating an account, you agree to our{' '}
                                        <a
                                            href="https://photoengineers.com/terms-of-service"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Terms of Service
                                        </a>{' '}
                                        and{' '}
                                        <a
                                            href="https://photoengineers.com/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Privacy Policy
                                        </a>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <div className="flex justify-start " id="captchaDiv"></div>
            </div>
        </>
    );
}

const validateEmail = (email: string) => {
    if (!email) {
        return false;
    }

    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
};
